import React, {useCallback, useEffect, useRef} from "react";
import {SequenceCompositionService} from "../services/SequenceCompositionService";
import {calculateAspectRatioFit} from "../../librettoEditor/utils/math/calculateAspectRatioFit";
import {SEQUENCE_CANVAS_ID, SEQUENCE_COMPOSITION_CONTAINER_ID} from "../config/config";
import {observer} from "mobx-react-lite";
import {throttle} from "lodash-es/lodash";
import {reaction} from "mobx";
import {SequenceStateStore} from "../store/SequenceStateStore";
import {SequenceWindowStore} from "../store/SequenceWindowStore";
import {SequenceApplicationStore} from "../store/SequenceApplicationStore";

// @ts-ignore
const SequenceCanvasSection = observer(({canvasRef, containerRef}) => {

    useEffect(() => {
        const disposer = reaction(
            () => ({
                canvasResolution: SequenceWindowStore.canvasResolution,
                selectedClipId: SequenceApplicationStore.selectedClipId,
                displayWidth: SequenceStateStore.display.width,
                displayHeight: SequenceStateStore.display.height,
                windowResolution: SequenceWindowStore.resolution,
            }),
            () => {
                updateCanvasSize();
            });

        return () => {
            disposer(); // Dispose of the autorun when the component unmounts
        };
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            // ResizeObserver for container size changes
            const resizeObserver = new ResizeObserver((entries) => {
                try {
                    for (const entry of entries) {
                        if (entry.target === containerRef.current) {
                            const {width, height} = entry.contentRect;
                            updateCanvasSize(); // Update canvas size based on container resize
                            SequenceWindowStore.setCanvasResolution([width, height]); // Sync with modal WindowStore
                        }
                    }
                } catch (e) {
                    console.error(e);
                }
            });
            resizeObserver.observe(containerRef.current);

            // MutationObserver for child DOM changes
            const mutationObserver = new MutationObserver(() => {
                initializeCanvas();
            });
            mutationObserver.observe(containerRef.current, {childList: true, subtree: true});

            // Initial canvas setup
            initializeCanvas();

            // Cleanup observers on component unmount
            return () => {
                resizeObserver.disconnect();
                mutationObserver.disconnect();
            };
        }
    }, [containerRef]);

    // Calculate the size for the canvas based on container dimensions
    const calculateWidthForComposition = () => {
        if (!containerRef.current || !canvasRef.current) return {newWidth: 0, newHeight: 0};

        const {clientWidth: containerWidth, clientHeight: containerHeight} = containerRef.current;
        const {width: canvasWidth, height: canvasHeight} = canvasRef.current;

        return calculateAspectRatioFit(canvasWidth, canvasHeight, containerWidth, containerHeight);
    };

    // Update the canvas size based on the container's dimensions
    const updateCanvasSize = useCallback(throttle(() => {
        if (canvasRef.current) {
            applyScale();
        }
    }, 100), []);

    const applyScale = () => {
        if (SequenceCompositionService.isInitialized() === false) return;

        console.log("Applying scale");
        const comp = SequenceCompositionService.composition;
        const baseW = comp.settings.width;
        const baseH = comp.settings.height;

        const container = containerRef.current!;
        const canvas = canvasRef.current!;

        // 1) Fix the canvas CSS size to the render resolution (once)
        canvas.style.width = `${baseW}px`;
        canvas.style.height = `${baseH}px`;

        // 2) Compute scale to fit container
        const scale = Math.min(container.clientWidth / baseW, container.clientHeight / baseH);

        // 3) Apply scale
        canvas.style.transform = `scale(${scale})`;
        canvas.style.transformOrigin = 'center';
    }

    // Initialize the canvas when the component mounts
    const initializeCanvas = () => {
        if (canvasRef.current) {
            // const selectionWatcher = SelectionWatcher.getInstance();
            // selectionWatcher.init(canvasRef.current);
            updateCanvasSize(); // Ensure the canvas size is updated after initialization
            SequenceCompositionService.mountComposition();
        }
    };

    const compositionStyles = {
        veComposition: {
            flexGrow: 1,      // Allow it to grow and take available space
            flexShrink: 1,    // Allow shrinking if necessary
            minWidth: 0,
            height: '100%',   // Ensure it takes the full height of its container
            width: "100%",
            position: 'relative',
            // zIndex: 1,
            // maxWidth: '40vw',
            minHeight: 0,
        },
        composition: {
            alignItems: 'center',
            // boxShadow: '0px 8px 32px 32px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box',
            display: 'flex',
            flexGrow: 1,      // Allow it to grow and take available space in the flexbox
            height: '90%',
            justifyContent: 'center',
            padding: '16px 16px calc(16px + 14px)',
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            minHeight: 0,
        },
        compositionContainer: {
            position: 'relative',
            display: 'grid',
            height: '80%',
            placeContent: 'center',
            width: '100%',
            maxWidth: '100%',
            minHeight: 0,
            flexGrow: 1,
            alignItems: "center",
            justifyContent: "center",
        },
        canvas: {
            width: '100%',
            height: 'auto',
        },
        relative: {
            position: 'relative',
        },
    };

    const handleMouseDown = () => {

    }

    return (
        <>
            {/* @ts-ignore */}
            <div style={compositionStyles.veComposition}>
                {/* @ts-ignore */}
                <div style={compositionStyles.composition} id={SEQUENCE_COMPOSITION_CONTAINER_ID} onMouseDown={handleMouseDown}>
                    {/* @ts-ignore */}
                    <div style={compositionStyles.compositionContainer} ref={containerRef}>
                        {/* @ts-ignore */}
                        <div style={compositionStyles.relative}
                             onMouseDown={(e) => {
                                 e.preventDefault();
                                 e.stopPropagation();
                             }}
                        >
                            <canvas id={SEQUENCE_CANVAS_ID} ref={canvasRef} style={compositionStyles.canvas}></canvas>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default SequenceCanvasSection;

